import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Star,
  Users,
  Video,
  Phone,
  MapPin,
  MoreHorizontal,
  CheckCircle,
  Calendar,
  AlertTriangle,
  X,
  Mail
} from 'lucide-react'
import { OneOnOneSessionWithUsers } from '@/types'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

const StarRating: React.FC<{ rating: number; label: string; color?: string }> = ({ rating, label, color = 'text-yellow-400' }) => (
  <div className="flex items-center gap-1">
    <span className="text-xs text-gray-600 min-w-[60px]">{label}:</span>
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} className={`h-3 w-3 ${star <= rating ? `${color} fill-current` : 'text-gray-300'}`} />
      ))}
    </div>
    <span className="text-xs text-gray-500">{rating}/5</span>
  </div>
)

interface SessionCardProps {
  session: OneOnOneSessionWithUsers
  onView?: () => void
  onDelete?: () => void
}

export const SessionCard: React.FC<SessionCardProps> = ({ session, onView, onDelete }) => {
  const getInitials = (name: string) => name.split(' ').map((n) => n[0]).join('').toUpperCase()

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
  }

  const getTypeIcon = () => {
    switch (session.type) {
      case 'Video Call':
        return <Video className="h-4 w-4 text-blue-600" />
      case 'Phone Call':
        return <Phone className="h-4 w-4 text-green-600" />
      case 'Email 1-on-1':
        return <Mail className="h-4 w-4 text-sky-600" />
      case 'Physical Meeting':
      default:
        return <MapPin className="h-4 w-4 text-purple-600" />
    }
  }

  const statusMeta = (() => {
    switch (session.status) {
      case 'completed':
        return { label: 'completed', icon: <CheckCircle className="h-4 w-4" />, textClass: 'text-green-600', bgClass: 'bg-green-100' }
      case 'cancelled':
        return { label: 'cancelled', icon: <X className="h-4 w-4" />, textClass: 'text-gray-600', bgClass: 'bg-gray-100' }
      case 'rescheduled':
        return { label: 'rescheduled', icon: <Calendar className="h-4 w-4" />, textClass: 'text-amber-600', bgClass: 'bg-amber-100' }
      case 'missed':
        return { label: 'missed', icon: <AlertTriangle className="h-4 w-4" />, textClass: 'text-red-600', bgClass: 'bg-red-100' }
      case 'scheduled':
      default:
        return { label: 'scheduled', icon: <Calendar className="h-4 w-4" />, textClass: 'text-blue-600', bgClass: 'bg-blue-100' }
    }
  })()

  const dueLabel = (() => {
    if (session.status === 'completed') return null
    if (session.dueInDays && session.dueDate) {
      return `Due in ${session.dueInDays} day${session.dueInDays === 1 ? '' : 's'} (${formatDate(session.dueDate)})`
    }
    if (session.dueInDays) {
      return `Due in ${session.dueInDays} day${session.dueInDays === 1 ? '' : 's'}`
    }
    if (session.dueDate) {
      return `Due by ${formatDate(session.dueDate)}`
    }
    return null
  })()

  const ratingsAvailable = Boolean(session.mood || session.productivityRating || session.teamTempRating)

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {getTypeIcon()}
            <span className={`flex items-center gap-2 text-xs font-medium capitalize rounded-full px-3 py-1 ${statusMeta.bgClass} ${statusMeta.textClass}`}>
              {statusMeta.icon}
              {statusMeta.label}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onView}>View Session</DropdownMenuItem>
              {onDelete && (
                <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                  Delete Session
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardTitle className="text-lg">{session.title}</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Users className="h-4 w-4 text-purple-600" />
              <span>Manager</span>
            </div>
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarImage src={session.manager.avatar} />
                <AvatarFallback className="text-xs bg-purple-100 text-purple-600">{getInitials(session.manager.name)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{session.manager.name}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Users className="h-4 w-4 text-green-600" />
              <span>Employee</span>
            </div>
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarImage src={session.employee.avatar} />
                <AvatarFallback className="text-xs bg-green-100 text-green-600">{getInitials(session.employee.name)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{session.employee.name}</span>
            </div>
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Type:</span>
            <div className="flex items-center gap-1">
              {getTypeIcon()}
              <span>{session.type}</span>
            </div>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Scheduled:</span>
            <span>{formatDate(session.scheduledDate)}, {formatTime(session.scheduledDate)}</span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Duration:</span>
            <span>{session.duration} min</span>
          </div>

          {session.completedAt && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Completed:</span>
              <span className="text-green-600">{formatDate(session.completedAt)}, {formatTime(session.completedAt)}</span>
            </div>
          )}

          {dueLabel && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Due:</span>
              <span className="text-amber-600">{dueLabel}</span>
            </div>
          )}
        </div>

        {ratingsAvailable && (
          <div className="space-y-2 pt-2 border-t">
            <h4 className="text-sm font-medium text-gray-700">Ratings</h4>
            <div className="space-y-1">
              {session.mood && <StarRating rating={session.mood} label="Mood" color="text-yellow-400" />}
              {session.productivityRating && <StarRating rating={session.productivityRating} label="Productivity" color="text-blue-400" />}
              {session.teamTempRating && <StarRating rating={session.teamTempRating} label="Team Temp" color="text-purple-400" />}
            </div>
          </div>
        )}

        {session.notes && (
          <div className="pt-2 border-t">
            <h4 className="text-sm font-medium text-gray-700 mb-1">Notes</h4>
            <p className="text-sm text-gray-600 line-clamp-2">{session.notes}</p>
          </div>
        )}

        <div className="flex justify-end pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onView} className="gap-2">
            View Session
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
